import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import {
  makeStyles,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Avatar,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Chip,
  Tooltip,
  Fade,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";

// Icons
import SearchIcon from "@material-ui/icons/Search";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import PersonIcon from "@material-ui/icons/Person";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import PhoneIcon from "@material-ui/icons/Phone";
import EmailIcon from "@material-ui/icons/Email";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import NewTicketModal from "../../components/NewTicketModal";
import { SocketContext } from "../../context/Socket/SocketContext";
import { CSVLink } from "react-csv";
import ImportContactsModal from "../../components/ImportContactsModal";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];
    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });
    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);
    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === "dark" ? "#0a0a0f" : "#f0f4f8",
    minHeight: "calc(100vh - 70px)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  // Header
  header: {
    marginBottom: theme.spacing(3),
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(3),
    gap: theme.spacing(2),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  titleSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  titleIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    boxShadow: "0 4px 14px rgba(99, 102, 241, 0.4)",
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  subtitle: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  // Actions
  actionsWrapper: {
    display: "flex",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: 1,
        minWidth: 120,
      },
    },
  },
  addButton: {
    background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
    color: "#fff",
    borderRadius: 10,
    padding: theme.spacing(1, 2.5),
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 4px 14px rgba(99, 102, 241, 0.4)",
    "&:hover": {
      background: "linear-gradient(135deg, #5558E3 0%, #7C4FE0 100%)",
      boxShadow: "0 6px 20px rgba(99, 102, 241, 0.5)",
    },
  },
  secondaryButton: {
    borderRadius: 10,
    padding: theme.spacing(1, 2),
    fontWeight: 600,
    textTransform: "none",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      borderColor: theme.palette.primary.main,
    },
  },
  // Search
  searchWrapper: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  searchField: {
    minWidth: 300,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: theme.palette.background.paper,
      transition: "all 0.2s ease",
      "&:hover": {
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
      },
    },
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
    },
  },
  // Stats
  statsRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
  },
  statCard: {
    padding: theme.spacing(2, 3),
    borderRadius: 12,
    background: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flex: 1,
    minWidth: 150,
  },
  statIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  // Table
  tableContainer: {
    borderRadius: 16,
    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
  },
  tableWrapper: {
    overflowX: "auto",
    maxHeight: "calc(100vh - 350px)",
    ...theme.scrollbarStyles,
  },
  table: {
    "& .MuiTableHead-root": {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
      "& .MuiTableCell-head": {
        fontWeight: 600,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        padding: theme.spacing(2),
        whiteSpace: "nowrap",
      },
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      transition: "all 0.15s ease",
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
      },
      "& .MuiTableCell-body": {
        padding: theme.spacing(1.5, 2),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
      },
    },
  },
  // Contact Row
  contactInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  contactAvatar: {
    width: 42,
    height: 42,
    borderRadius: 12,
    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  contactName: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: "0.9rem",
  },
  contactDetail: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
  },
  // Action Buttons
  actionBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
    },
  },
  whatsappBtn: {
    color: "#25D366",
    backgroundColor: alpha("#25D366", 0.1),
    "&:hover": {
      backgroundColor: alpha("#25D366", 0.2),
    },
  },
  editBtn: {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
    },
  },
  deleteBtn: {
    color: theme.palette.error.main,
    backgroundColor: alpha(theme.palette.error.main, 0.1),
    "&:hover": {
      backgroundColor: alpha(theme.palette.error.main, 0.2),
    },
  },
  // Empty state
  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
  },
  emptyIcon: {
    width: 80,
    height: 80,
    borderRadius: 20,
    background: alpha(theme.palette.primary.main, 0.1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
}));

const Contacts = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [openModalImport, setOpenModalImport] = useState(false);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/contacts/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-contact`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [socketManager]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleimportContact = async () => {
    try {
      await api.post("/contacts/import");
      history.go(0);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenImportModal = () => {
    setOpenModalImport(true);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleCloseModalImport = () => {
    setOpenModalImport(false);
  };

  return (
    <Box className={classes.root}>
      {/* Modals */}
      <ImportContactsModal open={openModalImport} onClose={handleCloseModalImport} />
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => handleCloseOrOpenTicket(ticket)}
      />
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        contactId={selectedContactId}
      />
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${deletingContact.name}?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() =>
          deletingContact ? handleDeleteContact(deletingContact.id) : handleimportContact()
        }
      >
        {deletingContact
          ? `${i18n.t("contacts.confirmationModal.deleteMessage")}`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>

      {/* Header */}
      <Fade in timeout={400}>
        <Box className={classes.header}>
          <Box className={classes.headerTop}>
            <Box className={classes.titleSection}>
              <Box className={classes.titleIcon}>
                <PersonIcon />
              </Box>
              <Box>
                <Typography className={classes.title}>
                  {i18n.t("contacts.title")}
                </Typography>
                <Typography className={classes.subtitle}>
                  Gerencie seus contatos e inicie conversas
                </Typography>
              </Box>
            </Box>

            <Box className={classes.actionsWrapper}>
              <Button
                variant="outlined"
                className={classes.secondaryButton}
                startIcon={<CloudUploadIcon />}
                onClick={handleOpenImportModal}
              >
                {i18n.t("contacts.buttons.import")}
              </Button>
              <CSVLink
                style={{ textDecoration: "none" }}
                separator=";"
                filename={"contatos.csv"}
                data={contacts.map((contact) => ({
                  name: contact.name,
                  number: contact.number,
                  email: contact.email,
                }))}
              >
                <Button
                  variant="outlined"
                  className={classes.secondaryButton}
                  startIcon={<CloudDownloadIcon />}
                >
                  {i18n.t("contacts.buttons.export")}
                </Button>
              </CSVLink>
              <Button
                variant="contained"
                className={classes.addButton}
                startIcon={<AddIcon />}
                onClick={handleOpenContactModal}
              >
                {i18n.t("contacts.buttons.add")}
              </Button>
            </Box>
          </Box>

          {/* Search */}
          <Box className={classes.searchWrapper}>
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              variant="outlined"
              size="small"
              value={searchParam}
              onChange={handleSearch}
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
            />
            <Chip
              label={`${contacts.length} contatos`}
              style={{
                background: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 600,
              }}
            />
          </Box>
        </Box>
      </Fade>

      {/* Table */}
      <Fade in timeout={600}>
        <Paper className={classes.tableContainer} elevation={0}>
          <Box className={classes.tableWrapper} onScroll={handleScroll}>
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("contacts.table.name")}</TableCell>
                  <TableCell align="center">{i18n.t("contacts.table.whatsapp")}</TableCell>
                  <TableCell align="center">{i18n.t("contacts.table.email")}</TableCell>
                  <TableCell align="center">{i18n.t("contacts.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell>
                      <Box className={classes.contactInfo}>
                        <Avatar
                          src={contact.profilePicUrl}
                          className={classes.contactAvatar}
                        >
                          {contact.name?.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography className={classes.contactName}>
                          {contact.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box className={classes.contactDetail}>
                        <PhoneIcon fontSize="small" />
                        {contact.number}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box className={classes.contactDetail}>
                        {contact.email && <EmailIcon fontSize="small" />}
                        {contact.email || "-"}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={1}>
                        <Tooltip title="Iniciar conversa">
                          <IconButton
                            size="small"
                            className={`${classes.actionBtn} ${classes.whatsappBtn}`}
                            onClick={() => {
                              setContactTicket(contact);
                              setNewTicketModalOpen(true);
                            }}
                          >
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            className={`${classes.actionBtn} ${classes.editBtn}`}
                            onClick={() => hadleEditContact(contact.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Can
                          role={user.profile}
                          perform="contacts-page:deleteContact"
                          yes={() => (
                            <Tooltip title="Excluir">
                              <IconButton
                                size="small"
                                className={`${classes.actionBtn} ${classes.deleteBtn}`}
                                onClick={() => {
                                  setConfirmOpen(true);
                                  setDeletingContact(contact);
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton avatar columns={3} />}
              </TableBody>
            </Table>

            {!loading && contacts.length === 0 && (
              <Box className={classes.emptyState}>
                <Box className={classes.emptyIcon}>
                  <PersonIcon style={{ fontSize: 40 }} />
                </Box>
                <Typography variant="h6" gutterBottom>
                  Nenhum contato encontrado
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Adicione seu primeiro contato ou importe uma lista
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
};

export default Contacts;
